/** @jsxImportSource @emotion/react */
import { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import CustomGrid from 'components/grids/CustomGrid';
import {
  SubTitleLayout,
  SubTitleGroup,
  GlobalBtnGroup,
  ControlBtnGroup,
} from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { useLoading } from 'components/process/Loading';
import * as input from '@grapecity/wijmo.input';
import { DataType } from '@grapecity/wijmo';
import { ItemList } from 'models/setup/excp/excp';
import { updateBoxProcedure } from 'apis/setup/excp/box';
import { useCommonModal } from 'hooks/useCommonModal';
import { DatePickerWrap } from 'components/inputs/DatePicker';
import Datepicker from 'react-tailwindcss-datepicker';
import dayjs from 'dayjs';
import useSessionStore from 'stores/useSessionStore';
import useEvent from 'react-use-event-hook';

type Props = {
  open: boolean;
  close: () => void;
  boxList: ItemList[];
};

interface ConditionProps {
  defaultDate?: string;
}

const SetupBoxArrivalPopup = ({ open, close, boxList }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { userId, langCd } = useSessionStore();

  const gridRef = useRef<any>();
  const [condition, setCondition] = useState<ConditionProps>({
    defaultDate: dayjs().format('YYYY.MM.DD'),
  });
  const [rowData, setRowData] = useState<ItemList[]>(boxList);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'pakBoxNo',
        header: String(t('com.label.Box No', 'Box No')),
        width: 100,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'pakItemNm',
        header: String(t('com.label.품목 목록명', '품목 목록명')),
        width: '*',
        isReadOnly: true,
        align: 'left',
      },
      {
        binding: 'mcodes',
        header: String(t('com.label.M-Code List', 'M-Code List')),
        width: 220,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'boxStatNm',
        header: String(t('com.label.현재상태', '현재상태')),
        width: 140,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'boxUnloDtm',
        header: String(t('com.label.도착처리일시', '도착처리일시')),
        width: 140,
        align: 'center',
        cssClass: 'WijmoDate',
        format: 'yyyy.MM.dd',
        dataType: DataType.String,
        editor: new input.InputDate(document.createElement('div'), {
          format: 'yyyy.MM.dd',
          max: new Date(),
        }),
      },
      {
        binding: 'inptDtm',
        header: String(t('com.label.Fab-In 일시', 'Fab-In 일시')),
        width: 140,
        align: 'center',
        cssClass: 'WijmoDate',
        format: 'yyyy.MM.dd',
        dataType: DataType.String,
        editor: new input.InputDate(document.createElement('div'), {
          format: 'yyyy.MM.dd',
          max: new Date(),
        }),
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    gridRef.current = grid;

    // 수정 불가능 한 cell css 처리
    grid.formatItem.addHandler((grid, e) => {
      if (e.panel !== grid.cells) return;

      const binding = grid.columns[e.col].binding;
      const item = grid.rows[e.row].dataItem;

      if (binding === 'boxUnloDtm' && item.arrivalYn !== 'Y') {
        e.cell.textContent = item[binding];
        e.cell.classList.add('cell-readonly');
      } else if (binding === 'inptDtm' && item.fabinYn !== 'Y') {
        e.cell.textContent = item[binding];
        e.cell.classList.add('cell-readonly');
      }
    });
  };

  const onBeginningEdit = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;

    // 수정 불가능 한 cell 처리
    if (binding === 'inptDtm') {
      if (item.fabinYn !== 'Y') e.cancel = true;

      // fab-in일시 선택 시 도착처리일시보다 작은 날짜를 선택 할 수 없도록 처리.
      const editor = grid.columns[e.col].editor;
      if (editor) {
        editor.min = item.boxUnloDtm;
      }
    } else if (item.arrivalYn !== 'Y' && item.fabinYn === 'Y' && binding === 'boxUnloDtm') {
      e.cancel = true;
    }
  });

  const btnApply = () => {
    const checkedList = gridRef.current.rows
      .filter((o) => o.isSelected)
      .map((o) => o.dataItem.pakBoxId);
    if (checkedList.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    setRowData((prev) => {
      return prev.map((o) => {
        if (checkedList.includes(o.pakBoxId)) {
          if (o.arrivalYn === 'Y') {
            return { ...o, boxUnloDtm: condition.defaultDate };
          } else if (o.fabinYn === 'Y') {
            // fab-in 일시 입력값이 도착처리일시보다 작은 경우 도착처리일시와 같은 날짜로 세팅
            const chkDtm =
              (condition.defaultDate ?? '') <= (o.boxUnloDtm ?? '')
                ? o.boxUnloDtm
                : condition.defaultDate;
            return { ...o, inptDtm: chkDtm };
          }
        }

        return o;
      });
    });
  };

  const btnSave = () => {
    if (rowData.some((o) => !o.boxUnloDtm && !o.inptDtm)) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.데이터를 모두 입력하세요.', '데이터를 모두 입력하세요.'),
      });
      return;
    }

    // save
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        openLoading(true);
        updateBoxProcedure({ boxList: rowData })
          .then((res) => {
            const data = res.data;
            // 일괄처리
            openCommonModal({
              content: ` 
            <p class="font-bold">
              ${t('setup.label.일괄 도착/Fab-In 처리 결과', '일괄 도착/Fab-In 처리 결과')}
            </p>
            <br>
            <div class="flex">
              <span style="width: 50px;">${t('setup.label.도착', '도착')}</span>
              : <span class="ml-3">${data?.arrival || 0} 건</span>
            </div>
            <div class="flex">
              <span style="width: 50px;">Fab-In</span>
              : <span class="ml-3">${data?.fabin || 0} 건</span>
            </div>
            <div class="flex">
              <span style="width: 50px;">${t('setup.label.성공', '성공')}</span>
              : <span class="ml-3">${data?.success || 0} 건</span>
            </div>
            <div class="flex">
              <span style="width: 50px;">${t('setup.label.실패', '실패')}</span>
              : <span class="ml-3">${data?.fail || 0} 건</span>
            </div>
            `,
            });
            close();
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t(
                'com.msg.도착/Fab-In 처리가 실패하였습니다.',
                '도착/Fab-In 처리가 실패하였습니다.'
              ),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  return (
    <CustomDialog
      title={String(t('setup.label.일괄 도착/Fab-In 처리', '일괄 도착/Fab-In 처리'))}
      size={'lg'}
      open={open}
      onClose={close}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('setup.label.기준일자', '기준일자')}</label>
                <DatePickerWrap className="single">
                  <Datepicker
                    useRange={false}
                    value={{
                      startDate: condition.defaultDate ?? null,
                      endDate: condition.defaultDate ?? null,
                    }}
                    onChange={(value) => {
                      setCondition((prev) => ({
                        ...prev,
                        defaultDate: dayjs(value?.startDate).format('YYYY.MM.DD') || '',
                      }));
                    }}
                    displayFormat={'YYYY.MM.DD'}
                    i18n={langCd}
                    inputClassName="datepickerInput"
                    asSingle={true}
                    maxDate={new Date()}
                  />
                </DatePickerWrap>
              </SearchCols>
            </SearchRows>
          </InputBox>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.Box List', 'Box List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="confirm" onClick={btnApply} disableRipple>
            {t('com.button.기준일자 적용', '기준일자 적용')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`setupBoxArrivalGridId`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isSelector={true}
        height={400}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
      />
      <GlobalBtnGroup>
        <Button css={IconButton.button} className="save" onClick={btnSave} disableRipple>
          {t('com.button.저장', '저장')}
        </Button>
      </GlobalBtnGroup>
    </CustomDialog>
  );
};

export default SetupBoxArrivalPopup;
